const VARIABLE_REFERENCE_PATTERN = /\[(@?[A-Z0-9_]+)(#D)?\]/gi;

function normalizeName(name) {
  return name.toUpperCase();
}

function stripCatalogSuffix(name) {
  return name.replace(/#D$/i, '');
}

function getReferenceType(name) {
  if (/^@(?:YES|NO|OK|EXIT|CANCEL|BTN(?!_?PRSD$)[A-Z0-9_]+)$/i.test(name)) {
    return 'button';
  }

  if (/^@/i.test(name)) {
    return 'system';
  }

  return 'variable';
}

function findCatalogForVariable(catalogs, variableName) {
  const normalizedVariable = normalizeName(variableName);

  return Object.values(catalogs).find((catalog) => normalizeName(catalog.variable) === normalizedVariable) || null;
}

function getNodeText(node) {
  return node.raw || node.expression || node.text || '';
}

function createDefinedVariableEntry(node, ir) {
  const catalog = findCatalogForVariable(ir.catalogs, node.name);

  return {
    name: node.name,
    definedAt: {
      line: node.line,
      raw: node.raw,
      datatype: node.datatype,
      length: node.length,
      required: Boolean(node.required),
    },
    hasDefaultCatalog: Boolean(catalog),
    catalog: catalog
      ? {
          id: catalog.id,
          sourceVariable: catalog.sourceVariable,
          line: catalog.line,
          endLine: catalog.endLine,
          optionCount: catalog.options.length,
        }
      : null,
    usageCount: 0,
    appearances: [
      {
        role: 'definition',
        nodeType: node.type,
        line: node.line,
        raw: node.raw,
      },
    ],
  };
}

function createReferenceAppearance(node, role, token, context = {}) {
  return {
    role,
    referenceType: context.referenceType || 'variable',
    windowId: context.windowId || null,
    nodeType: node.type,
    line: node.line,
    endLine: node.endLine || node.line,
    token,
    raw: getNodeText(node),
  };
}

function createExternalReferenceEntry(reference) {
  return {
    name: reference.baseName,
    referenceType: reference.referenceType,
    usageCount: 0,
    appearances: [],
  };
}

function getVariableCategory(variable) {
  if (variable.hasDefaultCatalog && variable.usageCount > 0) {
    return {
      rank: 1,
      label: 'catalogWithUsage',
    };
  }

  if (variable.hasDefaultCatalog) {
    return {
      rank: 2,
      label: 'catalogWithoutUsage',
    };
  }

  if (variable.usageCount > 0) {
    return {
      rank: 3,
      label: 'withoutCatalogWithUsage',
    };
  }

  return {
    rank: 4,
    label: 'definedNeverUsed',
  };
}

function getFirstUsage(variable) {
  const firstUsage = variable.appearances.find((appearance) => appearance.role === 'reference');

  if (!firstUsage) {
    return null;
  }

  return {
    line: firstUsage.line,
    nodeType: firstUsage.nodeType,
    token: firstUsage.token,
    raw: firstUsage.raw,
  };
}

function toVariableReportItem(variable) {
  const category = getVariableCategory(variable);

  return {
    category: category.label,
    name: variable.name,
    usageCount: variable.usageCount,
    hasDefaultCatalog: variable.hasDefaultCatalog,
    firstUsage: getFirstUsage(variable),
    appearances: variable.appearances,
  };
}

function collectNodeReferences(node) {
  const text = getNodeText(node);
  const references = [];
  let match;

  VARIABLE_REFERENCE_PATTERN.lastIndex = 0;

  while ((match = VARIABLE_REFERENCE_PATTERN.exec(text)) !== null) {
    references.push({
      token: match[0],
      baseName: stripCatalogSuffix(match[1]),
      isCatalogReference: Boolean(match[2]),
      referenceType: getReferenceType(match[1]),
    });
  }

  return references;
}

export function generateCrossReferenceReport(ast, ir) {
  const variables = {};
  const externalReferences = {};
  const windowReferences = {};
  let activeWindow = null;

  ast.nodes.forEach((node) => {
    if (node.type !== 'variable') {
      return;
    }

    variables[normalizeName(node.name)] = createDefinedVariableEntry(node, ir);
  });

  ast.nodes.forEach((node) => {
    if (node.type === 'window') {
      activeWindow = {
        id: node.id,
        line: node.line,
      };
      windowReferences[activeWindow.id] = {
        id: activeWindow.id,
        line: activeWindow.line,
        references: [],
      };
      return;
    }

    if (node.type === 'endwindow') {
      activeWindow = null;
      return;
    }

    if (node.type === 'variable') {
      return;
    }

    collectNodeReferences(node).forEach((reference) => {
      const context = {
        referenceType: reference.referenceType,
        windowId: activeWindow ? activeWindow.id : null,
      };

      if (activeWindow && node.type === 'unknown') {
        windowReferences[activeWindow.id].references.push({
          variable: reference.baseName,
          referenceType: reference.referenceType,
          line: node.line,
          nodeType: node.type,
          token: reference.token,
          raw: getNodeText(node),
        });
      }

      if (reference.referenceType !== 'variable') {
        const key = normalizeName(reference.baseName);

        if (!externalReferences[key]) {
          externalReferences[key] = createExternalReferenceEntry(reference);
        }

        externalReferences[key].usageCount += 1;
        externalReferences[key].appearances.push(createReferenceAppearance(node, 'reference', reference.token, context));
        return;
      }

      const variable = variables[normalizeName(reference.baseName)];

      if (!variable) {
        return;
      }

      if (node.type === 'default' && reference.isCatalogReference) {
        variable.appearances.push(createReferenceAppearance(node, 'defaultCatalog', reference.token, context));
        return;
      }

      variable.usageCount += 1;
      variable.appearances.push(createReferenceAppearance(node, 'reference', reference.token, context));
    });
  });

  const sortedVariables = Object.values(variables)
    .map((variable) => ({
      category: getVariableCategory(variable),
      variable,
    }))
    .sort((left, right) => {
      if (left.category.rank !== right.category.rank) {
        return left.category.rank - right.category.rank;
      }

      return left.variable.name.localeCompare(right.variable.name);
    })
    .map(({ variable }) => toVariableReportItem(variable));

  return {
    title: ir.title,
    variables: sortedVariables,
    externalReferences: Object.values(externalReferences).sort((left, right) => {
      if (left.referenceType !== right.referenceType) {
        return left.referenceType.localeCompare(right.referenceType);
      }

      return left.name.localeCompare(right.name);
    }),
    windowReferences: Object.values(windowReferences),
  };
}
